
import { Vue, Prop, Component, Emit } from "vue-property-decorator";
import { IPaging, IQuery, MetaParser, ObjectHelper } from "@root/core";
import { BaseTableCore } from "./base-table-core";
import { IUITagResult } from "@root/core/tags";
import { defaultPaginationData, getCellProperties, ITablePaginationData, ITablePaginationPortalData } from "./core";
import { ISearchHandle, uiSearch } from "@root/core/tags/extensions/ui-search";

const totalVisible = 7;

export interface IPortalTablePagination {
    pagination: string;
    search: string;
}

@Component
export default class TablePagination extends BaseTableCore {
    @Prop({ type: Object, required: false })
    declare data: ITablePaginationData & ITablePaginationPortalData;
    get mergedData() {
        return {
            ...defaultPaginationData,
            ...this.data,
        };
    }

    /// event handlers
    @Emit("update:paging")
    doUpdatePaging(newPaging: Partial<IPaging>): Partial<IPaging> | null {
        if (this.paging == null) return null;
        const clone = ObjectHelper.deepClone(this.paging);
        /// remove the param no-needed param: total, totalPages
        return Object.assign(
            {
                ...clone,
                total: undefined,
                totalPages: undefined,
            },
            newPaging,
        );
    }

    @Emit("update:searching")
    doUpdateSearching(newSearching: string): ISearchHandle | null {
        /// transform the search string into where criteria
        const result = getCellTableSearch(this.query, this.metas, this.viewInstance);
        /// sanity check: if no search then ignore
        if (result.specials?.search == null) return null;

        const rtn = uiSearch.handle(result.specials.search, newSearching);
        /// debugger: the return value of generated search params lies here.

        return rtn;
    }

    /// private helpers
    get paging() {
        return this.query.data?.paging;
    }
    get displayingPages() {
        return getDisplayingPages(this.paging);
    }
    get pageSizes() {
        return getPageSizes.call(this, this.paging);
    }
    get searchEnabled() {
        const result = getCellTableSearch(this.query, this.metas, this.viewInstance);
        return result.specials?.search != null;
    }
}

/// private helpers
// Which pages to display on the pagination?
function getDisplayingPages(paging: IPaging | undefined) {
    if (paging == null || paging.page == null || paging.totalPages == null) {
        console.error(
            `<Tablepagination> displayingPages should not be called if no paging or page or totalPages.`,
        );
        return;
    }

    let startPage, endPage;
    if (paging.totalPages <= totalVisible) {
        // Total pages less than total visible pages, show all pages
        startPage = 1;
        endPage = paging.totalPages;
    } else {
        // Calculate start and end page
        const maxPagesBeforeCurrentPage = Math.floor(totalVisible / 2);
        const maxPagesAfterCurrentPage = Math.ceil(totalVisible / 2) - 1;
        if (paging.page <= maxPagesBeforeCurrentPage) {
            // Near the beginning; no need to shift
            startPage = 1;
            endPage = totalVisible;
        } else if (paging.page + maxPagesAfterCurrentPage >= paging.totalPages) {
            // Near the end; shift the start back
            startPage = paging.totalPages - totalVisible + 1;
            endPage = paging.totalPages;
        } else {
            // Somewhere in the middle; shift the start back by maxPagesBeforeCurrentPage
            startPage = paging.page - maxPagesBeforeCurrentPage;
            endPage = paging.page + maxPagesAfterCurrentPage;
        }
    }

    // Create an array of pages from startPage to endPage
    let pages = Array.from(
        { length: endPage - startPage + 1 },
        (_, index) => startPage + index,
    );

    // Check if this.paging.page is less than this.paging.totalPages
    // and if it's not already included in the array
    if (paging.page > paging.totalPages && !pages.includes(paging.page)) {
        pages.push(paging.page);
    }

    // Sort the array since the current page might not be in order
    pages.sort((a, b) => a - b);

    return pages;
}

function getPageSizes(this: TablePagination, paging: IPaging | undefined) {
    const defaultPageSizes = [3, 5, 10, 20, 50];
    let pageSizes = [...defaultPageSizes];

    /// if pageSize not exists in default, add it in.
    if (paging?.pageSize && !pageSizes.includes(paging.pageSize)) {
        pageSizes.push(paging.pageSize);
        pageSizes = pageSizes.sort((a, b) => a - b);
    }

    return pageSizes.reduce((final, value) => {
        final[value] = this.$_("w_ShowEntries", [value]);
        return final;
    }, {});
}

function getCellTableSearch(
    query?: IQuery<any>,
    metas?: MetaParser,
    component?: Vue,
): IUITagResult {
    /// merge the results for the search
    let finalResult: IUITagResult = {};
    do {
        if (query == null || metas == null) break;

        for (let key in metas.attributes) {
            const result = getCellProperties(
                "TableSearch",
                query,
                undefined,
                metas,
                +key,
                component,
            );
            finalResult = ObjectHelper.deepMerge(finalResult, result);
        }
    } while (0);
    return finalResult;
}
